import React from "react";
import { Link } from "react-router-dom";
import BrandFooter from "@/byrd/BrandFooter";
import { LOGO_URL, CONTACT } from "@/byrd/data";
import { ArrowLeft } from "lucide-react";

/**
 * Privacy Policy for byrd-co.com. Covers the CRE brokerage marketing site,
 * the client document portal, the lender marketplace, and any data collected
 * by AI agents connected via API tokens or MCP. Content is a plain-English
 * summary — Wayne should replace with counsel-approved copy before any
 * regulated placement (state-level bank compliance, GDPR, CCPA disclosures).
 */
export default function PrivacyPolicyPage() {
  const lastUpdated = "February 7, 2026";
  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="privacy-policy-page">
      <header className="border-b border-[#E4DFD1] bg-white/80 backdrop-blur">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Byrd & CO" className="h-10 w-auto" />
            <div className="leading-tight hidden sm:block">
              <div className="font-serif text-lg font-bold">Byrd &amp; CO</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">Privacy Policy</div>
            </div>
          </Link>
          <Link to="/" className="byrd-btn byrd-btn-outline h-10 px-3 text-sm" data-testid="back-home">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-12 md:py-16">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#6B6558]">// Legal</div>
        <h1 className="font-serif text-4xl md:text-5xl font-bold mt-2 leading-tight">
          Privacy Policy
        </h1>
        <p className="text-[#6B6558] mt-3">Last updated: {lastUpdated}</p>

        <article className="prose prose-neutral max-w-none mt-8 space-y-6 text-[#2A2A2A]">
          <p>
            Byrd &amp; CO Commercial RE Lending ("<b>Byrd &amp; CO</b>", "we", "us")
            respects your privacy. This policy explains what we collect, how we use it,
            and the choices you have when you visit <b>byrd-co.com</b> or use our client
            document portal, lender marketplace, or Studio tools (Debt Quote Studio and
            Pre-Approval Letter Studio).
          </p>

          <Section title="1. Who this policy applies to">
            <ul className="list-disc pl-6 space-y-1">
              <li>Visitors to our public marketing site</li>
              <li>Prospective borrowers who submit a scenario, contact form, or callback request</li>
              <li>Clients who upload documents through the Byrd &amp; CO Client Document Portal</li>
              <li>Lending partners who register in our Lender Marketplace or receive term-sheet requests</li>
              <li>Listing agents, investors, builders, and developers who receive outreach from us</li>
              <li>Any user of our Debt Quote Studio or Pre-Approval Letter Studio</li>
            </ul>
          </Section>

          <Section title="2. Information we collect">
            <p><b>Directly from you:</b></p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name, email, phone number, company, and state — when you request a callback,
                register for the portal, or interact with our team</li>
              <li>Property, borrower, and financial information — required to underwrite and
                place your loan</li>
              <li>Documents you upload — tax returns, rent rolls, operating statements,
                identification, and other underwriting files</li>
              <li>Communications — emails, messages, and any content you send us</li>
            </ul>
            <p className="mt-3"><b>Collected automatically:</b></p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Log data (IP, browser type, timestamps) needed to operate and secure the site</li>
              <li>Cookies and similar technologies for authenticated portal sessions</li>
              <li>Usage analytics on how features are used, so we can improve the product</li>
            </ul>
            <p className="mt-3"><b>From third parties:</b></p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Public web sources (LinkedIn, Crexi, LoopNet, brokerage websites) to enrich
                outreach prospect records for lender / listing-agent / investor / builder /
                developer outreach programs</li>
              <li>Credit and background information from lending partners as part of the
                loan-placement process</li>
              <li>Marketing sources (Google Ads, referrals) that route you to our site</li>
            </ul>
          </Section>

          <Section title="3. How we use your information">
            <ul className="list-disc pl-6 space-y-1">
              <li>To place your loan — sharing your scenario and documents with lending
                partners you authorize us to approach</li>
              <li>To operate your Byrd &amp; CO account, Client Document Portal, and Studio
                tools</li>
              <li>To send you transactional emails (portal invites, deal updates,
                pre-approval letters, quotes, replies)</li>
              <li>To send outreach emails to lenders, listing agents, investors, builders, or
                developers when appropriate for the deal</li>
              <li>To generate branded PDFs (loan quotes, pre-approval letters, deal packages)</li>
              <li>To improve our service, prevent fraud, comply with law, and enforce our
                terms</li>
            </ul>
            <p className="mt-3 text-sm text-[#6B6558]">
              We do <b>not</b> sell your personal information. We do not share your
              underwriting file with any lender without your consent.
            </p>
          </Section>

          <Section title="4. Sharing with third parties">
            <p>We share information only with:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><b>Lending partners</b> you have authorized to review your scenario</li>
              <li><b>Service providers</b> that power our platform — including Postmark
                (transactional email), Backblaze B2 (encrypted document storage),
                Cloudflare (edge, DDoS protection), and MongoDB Atlas (database)</li>
              <li><b>AI providers</b> where we generate letters, quotes, drafts, or research
                — currently Anthropic (Claude), Google (Gemini), OpenAI, and Perplexity via
                the Emergent LLM proxy. Client documents are <b>not</b> submitted to these
                models without your explicit action inside the portal</li>
              <li><b>Analytics providers</b> — Google Ads (attribution) and internal audit
                logging</li>
              <li><b>Government agencies or law enforcement</b> when required by law or
                subpoena</li>
              <li><b>Successors</b> in the event of a merger, sale, or restructuring — you
                will be notified before your data is transferred</li>
            </ul>
          </Section>

          <Section title="5. AI-agent access to your data">
            <p>
              Byrd &amp; CO offers an authenticated API and a Model Context Protocol (MCP)
              server so that authorized AI agents (ChatGPT Custom GPTs, Claude Projects,
              scripts) can act on our data on <b>our behalf</b>. These agents:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Only run against a token minted by a Byrd &amp; CO administrator</li>
              <li>Can read and modify records but cannot delete data</li>
              <li>Cannot send external emails without explicit administrator approval — every
                outreach email, invite, or letter delivery is human-approved</li>
              <li>Are audit-logged per token so we can trace any action back to its source</li>
            </ul>
          </Section>

          <Section title="6. Data security">
            <ul className="list-disc pl-6 space-y-1">
              <li>All traffic is served over HTTPS with modern TLS</li>
              <li>Passwords are bcrypt-hashed; API tokens are SHA-256-hashed at rest and
                shown only once</li>
              <li>Optional two-factor authentication is available for portal accounts</li>
              <li>Document storage is encrypted at rest via Backblaze B2 server-side
                encryption</li>
              <li>Automated daily backups run off-site with retention</li>
              <li>Access to production systems is limited to a small number of authorized
                administrators and is audit-logged</li>
            </ul>
          </Section>

          <Section title="7. Retention">
            <p>
              We retain your information for as long as your account is active or as needed
              to provide services, comply with legal obligations, and enforce our agreements.
              When you close your account or request deletion, we deidentify or delete your
              personal information (except records we are legally required to retain, such as
              closed-loan documentation).
            </p>
          </Section>

          <Section title="8. Your rights">
            <p>Depending on where you live, you may have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Request access to the personal information we hold about you</li>
              <li>Correct or update inaccurate information</li>
              <li>Request deletion (subject to legal retention obligations)</li>
              <li>Opt out of marketing outreach — every marketing email includes an
                unsubscribe link</li>
              <li>Withdraw consent for a specific lender to receive your scenario</li>
            </ul>
            <p className="mt-3">
              To exercise any right, email{" "}
              <a href={`mailto:${CONTACT.wayne.email}`} className="text-[#C89434] hover:underline">
                {CONTACT.wayne.email}
              </a>
              . We respond within 30 days.
            </p>
          </Section>

          <Section title="9. Cookies">
            <p>
              We use a minimum of cookies — a session cookie for authenticated portal login,
              plus cookies set by Cloudflare (security) and Google Ads (attribution when you
              arrive from an ad). You can block cookies in your browser but the portal will
              not function without the session cookie.
            </p>
          </Section>

          <Section title="10. Children">
            <p>
              Byrd &amp; CO is a business service. We do not knowingly collect information
              from anyone under 18. If you believe a child has submitted information, contact
              us and we will delete it.
            </p>
          </Section>

          <Section title="11. Changes to this policy">
            <p>
              We may update this policy from time to time. Material changes will be posted
              here with a new "Last updated" date and, for portal accountholders, sent by
              email.
            </p>
          </Section>

          <Section title="12. Contact us">
            <p>
              Questions about this policy or your data? Reach out directly:
            </p>
            <div className="mt-3 space-y-1">
              <div><b>Byrd &amp; CO Commercial RE Lending</b></div>
              <div>
                Attn: {CONTACT.wayne.name}, {CONTACT.wayne.role || "Principal"}
              </div>
              <div>
                Email:{" "}
                <a href={`mailto:${CONTACT.wayne.email}`} className="text-[#C89434] hover:underline">
                  {CONTACT.wayne.email}
                </a>
              </div>
              <div>
                Phone:{" "}
                <a href={`tel:${CONTACT.wayne.phone}`} className="text-[#C89434] hover:underline">
                  {CONTACT.wayne.phone}
                </a>
              </div>
            </div>
          </Section>
        </article>
      </main>

      <BrandFooter />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-serif text-2xl font-bold mt-8 mb-2">{title}</h2>
      <div className="text-[15px] leading-relaxed">{children}</div>
    </section>
  );
}
